import { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();

    // Calculate cartTotal and itemCount BEFORE the return statement
    const cartTotal = cartItems.reduce((sum, item) => {
        const price = parseFloat(item.price) || 0;
        const quantity = parseInt(item.quantity) || 1;
        return sum + (price * quantity);
    }, 0);

    const itemCount = cartItems.reduce((sum, item) => {
        return sum + (parseInt(item.quantity) || 1);
    }, 0);

    useEffect(() => {
        const syncCart = async () => {
            if (user) {
                // If there's a guest cart in localStorage, migrate it to the database
                const guestCartStr = localStorage.getItem('guest_cart');
                if (guestCartStr) {
                    try {
                        const guestCart = JSON.parse(guestCartStr);
                        if (Array.isArray(guestCart) && guestCart.length > 0) {
                            setLoading(true);
                            for (const item of guestCart) {
                                await api.post('/api/cart', {
                                    menuItemId: item.menuItemId,
                                    quantity: item.quantity,
                                    specialInstructions: item.specialInstructions || '',
                                    restaurantId: item.restaurantId
                                });
                            }
                            localStorage.removeItem('guest_cart');
                        }
                    } catch (err) {
                        console.error('Failed to migrate guest cart:', err);
                    }
                }
                fetchCart(true);
            } else {
                // Load guest cart from localStorage if present
                const guestCartStr = localStorage.getItem('guest_cart');
                if (guestCartStr) {
                    try {
                        setCartItems(JSON.parse(guestCartStr));
                    } catch (e) {
                        setCartItems([]);
                    }
                } else {
                    setCartItems([]);
                }
            }
        };
        syncCart();
    }, [user]);

    const fetchCart = async (showLoading = false) => {
        if (!user) return;
        try {
            if (showLoading) setLoading(true);
            const response = await api.get('/api/cart');
            setCartItems(response.data);
        } catch (error) {
            console.error('Error fetching cart:', error);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const addToCart = async (menuItemId, quantity = 1, specialInstructions = '', targetRestaurantId = null, menuItemDetails = null) => {
        if (!user) {
            // Guest Cart Addition
            const guestCartStr = localStorage.getItem('guest_cart');
            let guestCart = [];
            if (guestCartStr) {
                try { guestCart = JSON.parse(guestCartStr); } catch(e) {}
            }
            
            // Check if item already exists in guest cart
            const existingIndex = guestCart.findIndex(item => item.menuItemId === menuItemId);
            if (existingIndex > -1) {
                guestCart[existingIndex].quantity += quantity;
            } else {
                guestCart.push({
                    id: 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                    menuItemId,
                    quantity,
                    specialInstructions,
                    restaurantId: targetRestaurantId,
                    name: menuItemDetails?.name || 'Item',
                    price: menuItemDetails?.price || 0,
                    image_url: menuItemDetails?.image_url || null
                });
            }
            
            localStorage.setItem('guest_cart', JSON.stringify(guestCart));
            setCartItems(guestCart);
            return { message: 'Added to guest cart' };
        }

        try {
            const payload = {
                menuItemId,
                quantity,
                specialInstructions
            };
            if (targetRestaurantId) {
                payload.restaurantId = targetRestaurantId;
            }
            const response = await api.post('/api/cart', payload);
            await fetchCart(false);
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const updateQuantity = async (itemId, quantity) => {
        if (!user) {
            const guestCartStr = localStorage.getItem('guest_cart');
            let guestCart = [];
            if (guestCartStr) {
                try { guestCart = JSON.parse(guestCartStr); } catch(e) {}
            }
            if (quantity < 1) {
                guestCart = guestCart.filter(item => item.id !== itemId);
            } else {
                guestCart = guestCart.map(item => item.id === itemId ? { ...item, quantity } : item);
            }
            localStorage.setItem('guest_cart', JSON.stringify(guestCart));
            setCartItems(guestCart);
            return;
        }

        if (quantity < 1) {
            await removeFromCart(itemId);
            return;
        }

        // Instant optimistic local state update for zero-latency UI response
        setCartItems(prev => prev.map(item => item.id === itemId ? { ...item, quantity } : item));

        try {
            await api.put(`/api/cart/${itemId}`, { quantity });
            const response = await api.get('/api/cart');
            setCartItems(response.data);
        } catch (error) {
            console.error('Error updating quantity:', error);
            fetchCart(false);
            throw error;
        }
    };

    const removeFromCart = async (itemId) => {
        if (!user) {
            const guestCartStr = localStorage.getItem('guest_cart');
            let guestCart = [];
            if (guestCartStr) {
                try { guestCart = JSON.parse(guestCartStr); } catch(e) {}
            }
            guestCart = guestCart.filter(item => item.id !== itemId);
            localStorage.setItem('guest_cart', JSON.stringify(guestCart));
            setCartItems(guestCart);
            return;
        }

        // Instant optimistic removal from local state
        setCartItems(prev => prev.filter(item => item.id !== itemId));

        try {
            await api.delete(`/api/cart/${itemId}`);
            const response = await api.get('/api/cart');
            setCartItems(response.data);
        } catch (error) {
            console.error('Error removing item:', error);
            fetchCart(false);
            throw error;
        }
    };

    const clearCart = async () => {
        if (!user) {
            localStorage.removeItem('guest_cart');
            setCartItems([]);
            return;
        }
        setCartItems([]);
        try {
            await api.delete('/api/cart');
        } catch (error) {
            console.error('Error clearing cart:', error);
        }
    };

    // Now return with all values defined
    return (
        <CartContext.Provider value={{
            cartItems,
            loading,
            addToCart,
            updateQuantity,
            removeFromCart,
            clearCart,
            cartTotal,
            itemCount
        }}>
            {children}
        </CartContext.Provider>
    );
};

export function useCart() {
    return useContext(CartContext);
}